import { Link } from 'react-router-dom';

import { Typography } from '@/ui';

import ConnectionStatus from '../ConnectionStatus';
import { LoadError, PageLoading } from '../PageState';
import RunStatusBanner from '../RunStatusBanner';
import { categoryMeta, NOTE_CATEGORIES, NOTES_GROUPS } from '../copy';
import { latestUpdatedAt, updatedLabel } from '../format';

import type { FC } from 'react';

import {
  useGetCoachingNotesQuery,
  useGetCoachingNotesSettingsQuery,
  useGetMcpConnectGrantsQuery,
} from '@/store/api/mcpMemberApi';

const Row: FC<{
  description: string;
  name: string;
  to: string;
  updated: string;
}> = ({ description, name, to, updated }) => (
  <Link
    className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-0 border-t border-solid border-cement-200 py-3.5 text-cement-500 no-underline first-of-type:border-t-0"
    to={to}
  >
    <span className="font-semibold">{name}</span>
    <span className="row-span-2 self-center text-sm text-cement-300">
      {updated}
    </span>
    <span className="text-sm text-cement-400">{description}</span>
  </Link>
);

const CoachingNotesListPage = () => {
  const notes = useGetCoachingNotesQuery();
  const grants = useGetMcpConnectGrantsQuery();
  const settings = useGetCoachingNotesSettingsQuery();

  if (notes.isLoading) {
    return <PageLoading />;
  }
  if (notes.isError || !notes.data) {
    return (
      <LoadError
        onRetry={() => {
          notes.refetch().catch(() => undefined);
        }}
      />
    );
  }
  const view = notes.data;

  return (
    <div>
      <div className="mb-6">
        <Typography component="h1" variant="h4">
          Coaching notes
        </Typography>
        <Typography className="mt-2 text-cement-400" variant="body1">
          What you’ve taken from your sessions, gathered from your reflections,
          plus the people involved. Mento updates these after each session. Edit
          or remove anything.
        </Typography>
      </div>
      {/* Unknown grants (loading or failed) must not read as "not shared". */}
      {grants.data && (
        <ConnectionStatus
          grants={grants.data.grants}
          settings={settings.data?.settings}
        />
      )}
      <RunStatusBanner run={view.run} />
      <section className="mb-8">
        <Typography
          className="mb-1 text-sm font-semibold uppercase text-cement-300"
          component="h2"
          variant="subtitle2"
        >
          {NOTES_GROUPS.notes}
        </Typography>
        {NOTE_CATEGORIES.map((category) => {
          const meta = categoryMeta(category, view.coach_first_name);
          return (
            <Row
              key={category}
              description={meta.description}
              name={meta.name}
              to={`/m/settings/coaching-notes/${category}`}
              updated={updatedLabel(view.updated_at[category])}
            />
          );
        })}
      </section>
      {view.people.length > 0 && (
        <section>
          <Typography
            className="mb-1 text-sm font-semibold uppercase text-cement-300"
            component="h2"
            variant="subtitle2"
          >
            {NOTES_GROUPS.people}
          </Typography>
          {view.people.map((person) => (
            <Row
              key={person.id}
              description={person.role}
              name={person.name}
              to={`/m/settings/coaching-notes/person-${person.id}`}
              updated={updatedLabel(latestUpdatedAt(person.items))}
            />
          ))}
        </section>
      )}
    </div>
  );
};

export default CoachingNotesListPage;
